import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SchemaRoundedIcon from '@mui/icons-material/SchemaRounded';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';
import GeneralLoaderWrapper from '@common/components/General/GeneralLoaderWrapper';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import Button from '@mui/material/Button';
import { ApiEndpointsIcon } from '@common/utils/icons/icons';
import ExternalApiEndpointCard from './ExternalApiEndpointCard';
import organizationsDataService from '../../../services/data/organizations-data-service';
import { externalApiSchemaUrl } from '../../../services/data/axios-base';
import { useAppSelector } from '../../../store';
import {
  selectExternalApi,
  selectOrganizationsData,
} from '../../../store/organization/organization-slice-selectors';
import {
  isEndpointCallableWithScopes,
  isEndpointVisibleToRole,
  listEndpoints,
  OpenApiDocument,
} from '../../../models/organization/openapi';

const OrganizationApiEndpoints: React.FC = () => {
  const { t } = useTranslation();

  const organizationData = useAppSelector((state) => selectOrganizationsData(state));
  const externalApi = useAppSelector((state) => selectExternalApi(state));

  const [schema, setSchema] = useState<OpenApiDocument | null>(null);
  const [fetchStatus, setFetchStatus] = useState<HttpFetchStatus>(HttpFetchStatus.Idle);

  useEffect(() => {
    let isCurrent = true;
    setFetchStatus(HttpFetchStatus.Loading);

    organizationsDataService
      .getExternalApiSchema()
      .then((document) => {
        if (isCurrent) {
          setSchema(document);
          setFetchStatus(HttpFetchStatus.Success);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setFetchStatus(HttpFetchStatus.Failed);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  const organizationRole = organizationData.current?.role ?? null;

  const grantedScopes = useMemo(
    () => Array.from(new Set((externalApi.data ?? []).flatMap((apiKey) => apiKey.scopes ?? []))),
    [externalApi.data],
  );

  const endpoints = useMemo(
    () =>
      listEndpoints(schema).filter((endpoint) =>
        isEndpointVisibleToRole(endpoint, organizationRole),
      ),
    [schema, organizationRole],
  );

  const openSchemaClickHandler = () => {
    window.open(externalApiSchemaUrl, '_blank');
  };

  return (
    <CustomSplitColumnCard
      title={t('title-api-endpoints')}
      content={[
        {
          left: (
            <Stack spacing={2} width="100%" py={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1}>
                  <ApiEndpointsIcon />
                  <Typography>{t('title-api-endpoint-list')}</Typography>
                </Stack>
                <Button startIcon={<SchemaRoundedIcon />} onClick={openSchemaClickHandler}>
                  {t('open-public-api-schema')}
                </Button>
              </Stack>
              <GeneralLoaderWrapper
                getStatus={fetchStatus}
                loadingMessage={t('loading-api-endpoints')}
                dataLoaded={schema != null}
                failedToLoadMessage={t('error-loading-api-endpoints')}
              >
                <Stack spacing={1}>
                  {endpoints.map((endpoint) => (
                    <ExternalApiEndpointCard
                      key={endpoint.id}
                      endpoint={endpoint}
                      isCallable={isEndpointCallableWithScopes(endpoint, grantedScopes)}
                    />
                  ))}
                </Stack>
              </GeneralLoaderWrapper>
            </Stack>
          ),
        },
      ]}
    />
  );
};

export default OrganizationApiEndpoints;
